import type { MarketplaceFulfillmentStatus } from "@nexus/contracts";
import { getLegacyLegalTransitions } from "@nexus/order-status";

import type {
  OrderLineVideo,
  OrderScriptRow,
  PartnerPreflightIntent,
} from "@/features/marketplace/types/marketplace.types";

import { isPartnerPreflightNotifyRetry } from "./partner-preflight-retry";

export type { PartnerPreflightIntent };

/** Matches the API abuse guard. Not an expected count. */
export const MAX_VIDEOS_PER_LINE = 50;

const CLOSED_TO_VIDEO_CHANGES = new Set<MarketplaceFulfillmentStatus>([
  "SUBMITTED_FOR_FINAL_APPROVAL",
  "ORDER_DELIVERED",
  "CANCELLED",
  "COMPLETED_PRE_ADGE",
]);

export function partnerLineVideos(script: OrderScriptRow): OrderLineVideo[] {
  return [...(script.videos ?? [])].sort((left, right) => left.position - right.position);
}

export function partnerVideoSelectionKey(video: OrderLineVideo): string {
  return video.id ?? `legacy-${video.position}`;
}

export function canChangePartnerVideos(
  status: MarketplaceFulfillmentStatus,
): boolean {
  return !CLOSED_TO_VIDEO_CHANGES.has(status);
}

export function canAddPartnerVideo(script: OrderScriptRow): boolean {
  if (!canChangePartnerVideos(script.fulfillmentStatus)) return false;
  const count = script.videoCount ?? script.videos?.length ?? 0;
  return count < MAX_VIDEOS_PER_LINE;
}

/** Whether the partner workspace should show the Upload tab (and upload entry points). */
export function partnerWorkspaceShowsUploadTab(script: OrderScriptRow): boolean {
  if (isPartnerPreflightNotifyRetry(script)) {
    return true;
  }
  if (script.notifyState === "PENDING_NOTIFY") {
    return false;
  }
  const canSubmitForFeedback = getLegacyLegalTransitions(
    script.fulfillmentStatus,
    "vendor",
  ).includes("SUBMITTED_FOR_FEEDBACK");
  const hasVideos = partnerLineVideos(script).length > 0;
  const canManageVideos =
    hasVideos && canChangePartnerVideos(script.fulfillmentStatus);
  return canSubmitForFeedback || canManageVideos;
}

/**
 * No videos: today's submit (client emails, video 1).
 * A stored video: replace that id.
 * A legacy in-memory video (no id): today's submit, which still replaces video 1.
 * Otherwise append.
 */
export function resolvePartnerPreflightIntent(
  hasVideos: boolean,
  replaceVideo: OrderLineVideo | null,
): PartnerPreflightIntent | undefined {
  if (!hasVideos) return undefined;
  if (replaceVideo) {
    return replaceVideo.id ? { deliverableId: replaceVideo.id } : undefined;
  }
  return { addVideo: true };
}

export function reviewIdAfterPartnerSubmit(
  script: OrderScriptRow | undefined,
  intent: PartnerPreflightIntent | undefined,
): string | null {
  if (!script) return null;
  if (intent && "deliverableId" in intent) {
    return (
      script.videos?.find((video) => video.id === intent.deliverableId)
        ?.scriptReviewId ?? script.scriptReviewId
    );
  }
  if (intent?.addVideo) {
    const added = script.videos?.[script.videos.length - 1];
    return added?.scriptReviewId ?? script.scriptReviewId;
  }
  return script.scriptReviewId;
}
